import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import MessageDetailsShape from 'app/shapes/MessageDetailsShape';

import {
  timestampInTimezone,
  ABBREVIATED_DATETIME_FORMAT,
} from 'app/helpers/timestampHelpers';
import { toCurrency } from 'app/helpers/currencyHelpers';

import { Box } from '@mui/material';

import MessageChip from 'app/features/mail/components/MessageChip';

const MessageDetailsHeader = ({
  messageDetails,
  currentUser,
  selectedTab,
}) => {
  // Memo Helpers
  const renderCustomerLink = (recipient) => {
    if (recipient.customerId) {
      return (
        <a
          key={recipient.login}
          href={`/my_customers/${recipient.customerId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {recipient.login}
        </a>
      );
    }
    return (
      <span>{recipient.login}</span>
    );
  };

  const buildToField = (messageDetails) => {
    let recipients = null;
    const maxDisplayRecipients = 2;

    if (currentUser.isAdvisor && messageDetails.sender.login === currentUser.login) {
      recipients = messageDetails?.recipients?.slice(0, maxDisplayRecipients).map((recipient, index) => renderCustomerLink(recipient, index));
    } else {
      recipients = messageDetails?.recipients?.slice(0, maxDisplayRecipients).map((recipient) => {
        return recipient.isAdvisor ? (
          <a
            key={recipient.login}
            href={`/${recipient.login}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {recipient.login}
          </a>
        ) : (
          recipient.login
        );
      });
    }

    const surplusCount = messageDetails.recipientCount - maxDisplayRecipients;
    return (
      <span>
        {recipients && recipients.reduce((prev, curr) => [prev, ', ', curr])}
        {surplusCount > 0 && ` +${surplusCount}`}
      </span>
    );
  };
  // End Memo Helpers

  // Memo Fields
  const toFromField = useMemo(() => {
    let senderText;
    if (messageDetails.sender.login === 'NF Mail Center') {
      senderText = messageDetails.sender.login;
    } else if (currentUser.isAdvisor) {
      senderText = messageDetails.sender.isAdvisor ? (
        <a
          href={`/${messageDetails.sender.login}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {messageDetails.sender.login}
        </a>
      ) : (
      // NOTE: When CUSTOMERS_21626 goes live, change this to a Link component since the UI will live in plus frontend
        renderCustomerLink(messageDetails.sender)
      );
    } else {
      senderText = messageDetails.sender.isAdvisor ? (
        <a
          href={`/${messageDetails.sender.login}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {messageDetails.sender.login}
        </a>
      ) : (
        messageDetails.sender.login
      );
    }

    return (
      <Box>
        {selectedTab === 'sent' ? 'To' : 'From'}
        :&nbsp;
        {selectedTab === 'sent' ? (buildToField(messageDetails)) : senderText}
      </Box>
    );
  }, [selectedTab, messageDetails]);

  const dateField = useMemo(() => (
    <Box>
      Date:&nbsp;
      {timestampInTimezone(messageDetails.createdAt, currentUser.canonical_iana_timezone, ABBREVIATED_DATETIME_FORMAT)}
    </Box>
  ), [messageDetails]);

  const transactionField = useMemo(() => {
    const messageIncludesTransaction = ['Tribute', 'Payment Request', 'Pay-to-View'].includes(messageDetails.messageType);
    return (
      <Box>
        { messageIncludesTransaction && toCurrency(messageDetails.paymentAmount) }
      </Box>
    );
  }, [messageDetails]);
  // End Memo Fields

  return (
    <>
      {toFromField}

      {dateField}

      { selectedTab !== 'notifications' && (
        <Box>
          Type:&nbsp;
          <MessageChip
            copy={messageDetails.messageType}
            sx={{ backgroundColor: '#999' }}
          />
        </Box>
      )}

      <Box>
        Subject:&nbsp;
        {messageDetails.subject}
      </Box>

      {transactionField}
    </>
  );
};

MessageDetailsHeader.defaultProps = {
};

MessageDetailsHeader.propTypes = {
  messageDetails: MessageDetailsShape.isRequired,
  currentUser: PropTypes.object.isRequired,
  selectedTab: PropTypes.string.isRequired,
};

export default MessageDetailsHeader;
